// OneSignal Web push service for the admin panel.
//
// Responsibilities:
//   - Initialize the OneSignal Web SDK so admins receive push notifications
//     (new messages, alerts) even when the browser tab is in background.
//   - Expose helpers to identify the logged-in admin and clean up on logout.
//
// If OneSignal is unavailable (missing key, SDK error, browser blocks SW),
// all calls fail silently — the rest of the app is unaffected.

import { saveWebDevice } from './adminService'
import { resolveWebNotificationRoute } from '../notifications/resolveWebNotificationRoute'

const APP_ID = process.env.VUE_APP_ONESIGNAL_APP_ID ?? '';

// So evita anexar os listeners duas vezes se setupNotificationHandlers for
// chamado mais de uma vez -- nao e um gate pra registerAdmin/unregisterAdmin,
// que usam o namespace do plugin (seguro de chamar a qualquer momento, ele
// enfileira internamente ate o SDK real carregar).
let _handlersAttached = false;

// Evita gravar o mesmo id repetidas vezes (o evento 'change' do PushSubscription
// pode disparar mais de uma vez pro mesmo id -- ex: re-registro no reload).
let _lastSavedId = null;

async function persistSubscriptionId(id) {
  if (!id || id === _lastSavedId) return;
  try {
    await saveWebDevice(id);
    _lastSavedId = id;
  } catch {
    // Falha de rede não deve travar o fluxo de notificação -- tenta de novo
    // na próxima mudança de subscription ou no próximo login.
  }
}

/**
 * Called once from main.js (via the OneSignal Vue plugin).
 * The Vue plugin handles the actual SDK init — this function registers
 * the foreground notification handler and marks the service as ready.
 *
 * @param {import('@onesignal/onesignal-vue3').OneSignalPlugin} OneSignal
 * @param {import('vue-router').Router} [router] usado pra navegar ao tocar
 *   numa notificação reconhecida por resolveWebNotificationRoute. Opcional
 *   pra não quebrar chamadas/testes existentes que não precisam de clique.
 */
export function setupNotificationHandlers(OneSignal, router) {
  if (!APP_ID || !OneSignal || _handlersAttached) return;
  try {
    // Show a browser notification even when the tab is in the foreground.
    OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
      try { event.getNotification().display(); } catch { /* ignore */ }
    });
    // Grava web_onesignal_id no backend assim que o navegador conceder a
    // subscrição -- sem isso, o envio de push pro Gerente (getManagerWebOnesignalIds)
    // nunca encontra ninguém pra notificar. O id pode não estar pronto
    // imediatamente após requestPermission(), por isso o listener de 'change'
    // além da checagem síncrona em registerAdmin.
    OneSignal.User.PushSubscription.addEventListener('change', (event) => {
      persistSubscriptionId(event?.current?.id);
    });
    // Primeiro listener de clique em push web do painel (ver ADR-0001, gap
    // deixado pra esta rodada). Tipos não reconhecidos não navegam.
    if (router) {
      OneSignal.Notifications.addEventListener('click', (event) => {
        const route = resolveWebNotificationRoute(event?.notification?.additionalData);
        if (route) router.push(route);
      });
    }
    _handlersAttached = true;
  } catch {
    // SDK error — continue without push notifications
  }
}

/**
 * Associate the logged-in admin with their OneSignal profile.
 * Allows the backend to target this specific device.
 *
 * @param {import('@onesignal/onesignal-vue3').OneSignalPlugin} OneSignal
 * @param {string} adminId
 */
export async function registerAdmin(OneSignal, adminId) {
  if (!OneSignal) return;
  try {
    OneSignal.login(String(adminId));
    await OneSignal.Notifications.requestPermission();
    // Se a subscrição já existir (ex: permissão concedida numa sessão
    // anterior), o evento 'change' não dispara de novo -- checa direto aqui.
    await persistSubscriptionId(OneSignal.User.PushSubscription.id);
  } catch {
    // ignore — browser may have denied permissions
  }
}

/**
 * Disassociate this device from any OneSignal profile on logout.
 *
 * @param {import('@onesignal/onesignal-vue3').OneSignalPlugin} OneSignalVue
 */
export async function unregisterAdmin(OneSignal) {
  if (!OneSignal) return;
  try {
    OneSignal.logout();
  } catch {
    // ignore
  }
}
